import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardCheck } from 'lucide-react';
import {
  clearStoredGuestTokenForCredentials,
  fetchGuestSession,
  getStoredGuestTokenForCredentials,
  GUEST_EVALUATION_TOKEN_KEY,
  storeGuestTokenForCredentials,
  verifyGuestAccess
} from '../lib/guestEvaluationsApi';
import SubtleFooter from '../components/SubtleFooter';

type AccessSituationType =
  | 'missing_input'
  | 'already_completed'
  | 'expired'
  | 'invalid_credentials'
  | 'not_found'
  | 'temporary_error';

type AccessSituation = {
  type: AccessSituationType;
  title: string;
  message: string;
  reasoning?: string;
  backendMessage?: string;
};

const classifyAccessError = (rawMessage: string): AccessSituation => {
  const message = rawMessage.trim();
  const normalized = message.toLowerCase();

  if (!message) {
    return {
      type: 'temporary_error',
      title: 'Situatie: tijdelijke fout',
      message: 'We konden je aanvraag niet verwerken. Probeer het zo opnieuw.',
      reasoning: 'Geen fouttekst ontvangen van de backend.',
    };
  }

  if (normalized.includes('already_completed') || normalized.includes('already completed')) {
    return {
      type: 'already_completed',
      title: 'Situatie: evaluatie al afgerond',
      message: 'Deze evaluatie is al definitief ingediend. Inloggen met deze code is niet meer nodig.',
      reasoning: 'Match op backend-fouttekst: "already_completed" of "already completed".',
      backendMessage: message,
    };
  }

  if (normalized.includes('expired') || normalized.includes('verlopen') || normalized.includes('timeout')) {
    return {
      type: 'expired',
      title: 'Situatie: code of sessie verlopen',
      message: 'De toegang is verlopen. Vraag een nieuwe code aan bij de coach/beheerder.',
      reasoning: 'Match op backend-fouttekst: "expired", "verlopen" of "timeout".',
      backendMessage: message,
    };
  }

  if (
    normalized.includes('invalid') ||
    normalized.includes('incorrect') ||
    normalized.includes('ongeldig') ||
    normalized.includes('onjuist') ||
    normalized.includes('401') ||
    normalized.includes('403')
  ) {
    return {
      type: 'invalid_credentials',
      title: 'Situatie: onjuiste gegevens',
      message: 'E-mail of code klopt niet. Controleer beide velden en probeer opnieuw.',
      reasoning: 'Match op backend-fouttekst: invalid/incorrect/401/403.',
      backendMessage: message,
    };
  }

  if (normalized.includes('not found') || normalized.includes('niet gevonden') || normalized.includes('404')) {
    return {
      type: 'not_found',
      title: 'Situatie: evaluatie niet gevonden',
      message: 'Er is geen evaluatie gevonden voor deze combinatie van e-mail en code.',
      reasoning: 'Match op backend-fouttekst: not found/niet gevonden/404.',
      backendMessage: message,
    };
  }

  return {
    type: 'temporary_error',
    title: 'Situatie: tijdelijke fout',
    message: message || 'Er ging iets mis bij het controleren van je gegevens.',
    reasoning: 'Geen bekende foutcategorie gematcht; fallback gebruikt.',
    backendMessage: message,
  };
};

const GuestEvaluationAccess: React.FC = () => {
  const CODE_LENGTH = 6;
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [codeDigits, setCodeDigits] = useState<string[]>(Array.from({ length: CODE_LENGTH }, () => ''));
  const [loading, setLoading] = useState(false);
  const [situation, setSituation] = useState<AccessSituation | null>(null);
  const codeInputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const code = codeDigits.join('');

  const focusCodeInput = (index: number) => {
    const target = codeInputRefs.current[index];
    if (target) {
      target.focus();
      target.select();
    }
  };

  const handleCodeDigitChange = (index: number, rawValue: string) => {
    const digit = rawValue.replace(/\D/g, '').slice(-1);
    setCodeDigits((prev) => {
      const next = [...prev];
      next[index] = digit;
      return next;
    });

    if (digit && index < CODE_LENGTH - 1) {
      focusCodeInput(index + 1);
    }
  };

  const handleCodeKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && !codeDigits[index] && index > 0) {
      event.preventDefault();
      setCodeDigits((prev) => {
        const next = [...prev];
        next[index - 1] = '';
        return next;
      });
      focusCodeInput(index - 1);
      return;
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      focusCodeInput(index - 1);
      return;
    }

    if (event.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
      event.preventDefault();
      focusCodeInput(index + 1);
    }
  };

  const handleCodePaste = (index: number, event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pastedDigits = event.clipboardData.getData('text').replace(/\D/g, '');
    if (!pastedDigits) return;

    const limitedDigits = pastedDigits.slice(0, CODE_LENGTH - index);
    setCodeDigits((prev) => {
      const next = [...prev];
      limitedDigits.split('').forEach((digit, offset) => {
        next[index + offset] = digit;
      });
      return next;
    });

    const nextIndex = Math.min(index + limitedDigits.length, CODE_LENGTH - 1);
    focusCodeInput(nextIndex);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSituation(null);

    const normalizedCode = code.trim();
    if (!email.trim() || normalizedCode.length !== CODE_LENGTH) {
      setSituation({
        type: 'missing_input',
        title: 'Situatie: gegevens ontbreken',
        message: 'Vul zowel e-mail als code in.'
      });
      return;
    }

    setLoading(true);
    try {
      const normalizedEmail = email.trim();

      const storedToken = getStoredGuestTokenForCredentials(normalizedEmail, normalizedCode);
      if (storedToken) {
        try {
          await fetchGuestSession(storedToken);
          sessionStorage.setItem(GUEST_EVALUATION_TOKEN_KEY, storedToken);
          navigate('/evaluation-fill');
          return;
        } catch {
          clearStoredGuestTokenForCredentials(normalizedEmail, normalizedCode);
        }
      }

      const result = await verifyGuestAccess(normalizedEmail, normalizedCode);
      sessionStorage.setItem(GUEST_EVALUATION_TOKEN_KEY, result.token);
      storeGuestTokenForCredentials(normalizedEmail, normalizedCode, result.token);
      navigate('/evaluation-fill');
    } catch (submitError) {
      const message = submitError instanceof Error ? submitError.message : 'Verificatie mislukt.';
      if (message.toLowerCase().includes('already_completed')) {
        clearStoredGuestTokenForCredentials(email.trim(), code.trim());
        sessionStorage.removeItem(GUEST_EVALUATION_TOKEN_KEY);
      }
      setSituation(classifyAccessError(message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="flex-1 flex items-center justify-center px-4 py-8 md:py-12">
        <div className="w-full max-w-lg space-y-6">
          <div className="flex justify-center">
            <img src="/we-dohockey-orange-black-trans.png" alt="doHockey" className="h-16 md:h-20 w-auto object-contain" />
          </div>
          <div className="bg-white border border-gray-200 rounded-xl shadow-md p-6 space-y-4">
          <div className="flex items-center gap-2">
            <ClipboardCheck size={20} className="text-orange-600" />
            <h1 className="text-xl font-semibold text-gray-900">Evaluatie toegang</h1>
          </div>
          <p className="text-sm text-gray-600">
            Vul je e-mailadres en 6-cijferige code in om de evaluatie te openen.
          </p>

          <form onSubmit={handleSubmit} className="space-y-3">
            <label className="block space-y-1">
              <span className="text-sm font-medium text-gray-700">E-mail</span>
              <input
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                type="email"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none"
              />
            </label>
            <label className="block space-y-1">
              <span className="text-sm font-medium text-gray-700">Code</span>
              <div className="flex items-center gap-2">
                {codeDigits.map((digit, index) => (
                  <input
                    key={`code-digit-${index}`}
                    ref={(element) => {
                      codeInputRefs.current[index] = element;
                    }}
                    value={digit}
                    onChange={(event) => handleCodeDigitChange(index, event.target.value)}
                    onKeyDown={(event) => handleCodeKeyDown(index, event)}
                    onPaste={(event) => handleCodePaste(index, event)}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete={index === 0 ? 'one-time-code' : 'off'}
                    maxLength={1}
                    className="h-11 w-11 rounded-md border border-gray-300 text-center text-base font-semibold text-gray-800 focus:border-orange-500 focus:outline-none"
                    aria-label={`Code cijfer ${index + 1}`}
                  />
                ))}
              </div>
            </label>

            {situation && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                <p className="font-semibold">{situation.title}</p>
                <p>{situation.message}</p>
                {(situation.reasoning || situation.backendMessage) && (
                  <div className="mt-2 rounded border border-red-200 bg-white px-2 py-1 text-xs text-red-800">
                    <p><strong>Afweging:</strong> {situation.reasoning || '-'}</p>
                    <p className="font-mono"><strong>Backend:</strong> {situation.backendMessage || '-'}</p>
                  </div>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center rounded-md bg-primary-600 text-white px-3 py-2 text-sm font-medium hover:bg-primary-700 disabled:opacity-60"
            >
              {loading ? 'Controleren...' : 'Verder'}
            </button>
          </form>
          </div>
        </div>
      </div>
      <SubtleFooter />
    </div>
  );
};

export default GuestEvaluationAccess;
